/**
 * Enforces image distribution invariants on a generated carousel.
 *
 * Rules (per spec 2026-04-22-chat-first-carousel):
 * - Cover (first slide, SlideType.COVER) ALWAYS has an image.
 * - CTA (last slide, SlideType.CTA) NEVER has an image.
 * - If format === 'list': every slide that appears to present a numbered item gets an image.
 * - If format === 'narrative': ~50% of CONTENT slides get images, distributed with a slight late-weight bias.
 *
 * The AI is prompted to follow these rules via `needsImage`; this function is a
 * post-processing guard that enforces the invariants even if the model disobeys.
 */

import { Slide, SlideType } from '../types';

export type CarouselFormat = 'narrative' | 'list';

/** Detects whether a slide's content begins with a numbered marker like "1.", "1)", "#1", etc. */
function looksLikeNumberedItem(slide: Slide): boolean {
  const trimmed = slide.content.trim();
  return /^(#?\s*\d+[.\)\-:]|\*\*\d+[.\)\-:])/.test(trimmed);
}

/**
 * Picks which CONTENT slides (excluding cover and CTA) receive images for the narrative format.
 * Distributes ~50% evenly with a slight late-weight bias. For reference:
 *   10 slides → indices 2, 4, 6, 7 (slides 3, 5, 7, 8)
 *   7 slides  → indices 2, 4 (slides 3, 5)
 *   5 slides  → indices 2 (slide 3)
 *   3 slides  → none (only cover)
 */
function pickNarrativeMiddleIndices(totalSlides: number): number[] {
  const middleCount = Math.max(0, totalSlides - 2);
  const targetImages = Math.floor(middleCount / 2);
  if (targetImages === 0) return [];

  // Special case: for small carousels, just pick slide 3 (index 2).
  if (targetImages === 1) return [2];

  // Distribute (target - 1) picks at even positions starting at index 2, step 2.
  // Then add the penultimate-middle index as the "late-weight bias" pick.
  //
  // Reference mapping (produces the spec-approved distribution):
  //   N=10 → [2, 4, 6, 7]   (slides 3, 5, 7, 8)
  //   N=7  → [2, 4]         (slides 3, 5)
  //   N=5  → [2]            (slide 3)
  //   N=3  → []             (only cover)
  const picks: number[] = [];
  for (let i = 0; i < targetImages - 1; i++) {
    picks.push(2 + i * 2);
  }
  const penultimateMiddle = totalSlides - 3; // index just before the CTA slide
  if (penultimateMiddle > 0 && !picks.includes(penultimateMiddle)) {
    picks.push(penultimateMiddle);
  }
  return picks;
}

/**
 * Returns a new array of slides with `showImage` normalized per the rules.
 * Does not mutate the input.
 */
export function applyImageDistributionRules(
  slides: Slide[],
  format: CarouselFormat,
): Slide[] {
  if (slides.length === 0) return slides;

  const totalSlides = slides.length;
  const lastIndex = totalSlides - 1;

  let allowedIndices: Set<number>;

  if (format === 'list') {
    // Cover + every numbered-item slide, CTA excluded.
    const numberedIndices = slides
      .map((slide, idx) => (idx !== 0 && idx !== lastIndex && looksLikeNumberedItem(slide) ? idx : -1))
      .filter((idx) => idx >= 0);
    allowedIndices = new Set<number>([0, ...numberedIndices]);
  } else {
    const middleIndices = pickNarrativeMiddleIndices(totalSlides);
    allowedIndices = new Set<number>([0, ...middleIndices]);
  }

  // Forcing showImage=true only makes sense when the slide also has a
  // usable imagePrompt — otherwise the orchestrator can't generate an image
  // and the thumbnail is left stuck rendering a placeholder that never
  // resolves. If the AI skipped the prompt, respect that by leaving
  // showImage=false.
  const hasUsablePrompt = (slide: Slide): boolean =>
    !!slide.imagePrompt && slide.imagePrompt.trim().length > 0;

  return slides.map((slide, idx) => {
    if (idx === lastIndex) return { ...slide, showImage: false };      // CTA forced false
    if (idx === 0) {
      return { ...slide, showImage: hasUsablePrompt(slide) };          // cover: true only if prompt exists
    }
    return { ...slide, showImage: allowedIndices.has(idx) && hasUsablePrompt(slide) };
  });
}
